import { FlattenedDroppedData, SEP, WebkitFile, isNotOSFile } from '@myua/dropzone';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import mapKeys from 'lodash/mapKeys';
import mapValues from 'lodash/mapValues';
import zipObject from 'lodash/zipObject';
import Notifications from 'react-notification-system-redux';
import { v1 as uuid } from 'uuid';
import { api } from '../../../general/apiScenarios';
import { detectFonts } from '../../../general/fontLicense/fontDetection';
import { requestLicenseConfirmation } from '../../../general/fontLicense/LicenseConfirmationService';
import { bindLicenseIdsToFiles } from '../../../general/fontLicense/licenseRegistry';
import { INode, NodeFlag } from '../../../general/model';
import { INodeToCHNode } from '../../../general/node';
import { Interrupt, TSagaReturn } from '../../../general/scenarioFactory';
import { nonNull } from '../../../general/utils/nonNull';
import { _select, _take, ListenerApiLight, lm } from '../../listenerMiddleware';
import { logger } from '../../logger';
import { getNodeId, getSettings } from '../app/selectors';
import { getBreadcrumbs } from '../breadcrumbs/selectors';
import * as conflictResolverActions from '../conflictResolver/actions';
import { AutoConflictResolution } from '../conflictResolver/model';
import { resolve } from '../conflictResolver/sagas';
import { getCreativeFolder } from '../breadcrumbs/selectors';
import * as fileManagerActions from '../fileManager/actions';
import { apiLoadNodes2 } from '../fileManager/sagas';
import { tagWindow } from '../tag/sagas';
import * as uploaderActions from './actions';
import { clearRegistry, dropFile } from './fileRegistry';
import {
  Job,
  JobStatus,
  Messages,
  hasInvalidNameChars,
  jobIsDisposable,
  jobIsTagReminder,
  makeJob,
  makeKey,
} from './model';
import { getJobs, getJobsKeyed, getGroupNames } from './selectors';
import { uploadScenario } from './uploadSingleFile.saga';

import { uploadWorkflow } from '../creativeWorkflow/workflows/uploadWorkflow';

import { partition } from 'lodash';

// Parallel upload configuration
const MAX_PARALLEL_UPLOADS = 3; // Maximum number of parallel uploads

type GetMappingsEntry = {
  file: WebkitFile;
  targetNodeID: number;
  targetRoot: string;
};

export function uploaderRootListener() {
  // Release File references (MYUA-16384) when jobs leave the store. Files stay in
  // the registry through UPLOAD_FAILURE/SKIPPED so retry/restart can re-read them;
  // they are only dropped on terminal success (see processJob), on group removal,
  // and on a full cancel.
  // eslint-disable-next-line @typescript-eslint/require-await
  lm.takeEvery([uploaderActions.cancelAllJobs], async function () {
    // cancelAllJobs resets the uploader state to initialState (jobs = []), so no
    // job can be retried afterwards — release every registered File.
    clearRegistry();
  });
  // eslint-disable-next-line @typescript-eslint/require-await
  lm.takeEvery([uploaderActions.groupDeleted], async function (action, lapi) {
    // The reducer has already removed the group's jobs from state by the time this
    // effect runs, so read the pre-reducer state to learn which uuids were dropped.
    const removedJobs = getJobs(lapi.getOriginalState()).filter(
      (job) => job.groupID === action.payload.groupID,
    );
    removedJobs.forEach((job) => dropFile(job.uuid));
  });
  lm.takeLatest(uploaderActions.setTagsButtonClicked, async function (action, lapi) {
    const jobsKeyed = _select(lapi, getJobsKeyed);
    const jobs = jobsKeyed[action.payload.groupID];
    if (jobs.length === 0) return;
    if (!jobs.every(jobIsTagReminder)) return;
    const nodeIDs = jobs.map((j) => j.createdNode).filter(nonNull);
    const success = await tagWindow(lapi, nodeIDs);
    lapi.signal.throwIfAborted();
    if (!success) return;
    jobs.forEach((job) =>
      lapi.dispatch(
        uploaderActions.jobUpdated({
          jobID: job.uuid,
          jobProps: {
            status: JobStatus.FINISH,
          },
        }),
      ),
    );
  });
  lm.takeEvery([uploaderActions.filesSelectedForUpload], async function (action, lapi) {
    const files: FlattenedDroppedData = mapValues(action.payload.files, (v) =>
      v.filter(isNotOSFile),
    );

    // Single chokepoint for both upload paths (creative workflow and legacy folder):
    // fonts (incl. fonts inside .zip) require a confirmed license before uploading.
    // Gated per-instance: only run the flow when the backend
    // `validate_font_license_before_uploading` setting is on (My.Games instance).
    // Absent -> false -> skip entirely.
    const allFiles = Object.values(files).flat();
    const validateFontLicense =
      _select(lapi, getSettings)?.validate_font_license_before_uploading ?? false;
    const fonts = validateFontLicense ? await detectFonts(allFiles) : [];
    lapi.signal.throwIfAborted();
    if (fonts.length > 0) {
      const submission = await requestLicenseConfirmation(fonts, lapi.signal);
      // Cancel or an aborted signal -> do not start the upload (blocking form).
      if (!submission || lapi.signal.aborted) return;
      // Bind the created license ids to their concrete File (an archive gets the ids
      // of every font found inside it). The same File reference is read back at
      // upload time (via fileRegistry), so keys match 1:1 and same-named files
      // never collide.
      bindLicenseIdsToFiles(allFiles, fonts, submission);
    }

    const cf = _select(lapi, getCreativeFolder);

    if (cf !== undefined) {
      // мы в креативе, надо почикать все файлы внутри папок
      const cleanFiles = files[''] ?? [];
      const paths = Object.keys(files);
      if (paths.length > 1 || paths[0] !== '') {
        // есть что-то еще кроме SEP
        lapi.dispatch(Notifications.warning(Messages.cantImportFolders()));
      }
      const parentNodeID = _select(lapi, getNodeId);
      if (parentNodeID === null) return;
      await uploadWorkflow(lapi, cleanFiles, parentNodeID);
    } else {
      await uploadFolder(lapi, files);
    }
  });
  lm.takeEvery([uploaderActions.workflowUploadRequested], async function (action, lapi) {
    const { groupID, groupName, files, parentNodeID, parentIsAssetLab } = action.payload;

    const newJobGroups = [
      {
        groupKey: groupName,
        groupID,
        newJobs: files.map((file) => makeJob(file, parentNodeID, groupID, parentIsAssetLab)),
      },
    ];

    if (!(await validateFiles(newJobGroups))) {
      lapi.dispatch(Notifications.error(Messages.validateFileNamesFail()));
      return;
    }

    lapi.dispatch(fileManagerActions.refreshRequested());

    await checkExistantFiles(newJobGroups, lapi);
    lapi.signal.throwIfAborted();

    newJobGroups.forEach((group) => {
      const insertedJobs = insertJobs(lapi, group.newJobs);
      if (insertedJobs.length === 0) {
        return;
      }

      lapi.dispatch(
        uploaderActions.groupInserted({
          groupID: group.groupID,
          groupName: group.groupKey,
        }),
      );
    });
  });
  lm.takeLatest(
    [uploaderActions.jobUpdated, uploaderActions.groupDeleted],
    async function (_, lapi) {
      const jobs = _select(lapi, getJobs);
      if (jobs.every(jobIsDisposable)) {
        const result = await Promise.race([
          lapi.delay(3000),
          _take(lapi, uploaderActions.groupRestarted, uploaderActions.jobsInserted),
        ]);
        if (lapi.signal.aborted) {
          lapi.dispatch(uploaderActions.cancelAllJobs());
        }
        if (result === null) {
          lapi.dispatch(uploaderActions.cancelAllJobs());
        }
      }
    },
  );

  lm.takeLeading(
    [uploaderActions.jobsInserted, uploaderActions.groupRestarted],
    async function (action, lapi) {
      const running = new Set();
      // If groupRestarted triggered this handler, re-run validation & conflict checks for
      // previously-skipped jobs so that the user is prompted to resolve overwrites instead
      // of silently overwriting files on restart.
      if (action && action.type === uploaderActions.groupRestarted.type) {
        const groupID = action.payload.groupID;
        const jobs = _select(lapi, getJobs).filter((j) => j.groupID === groupID);
        // Only operate on jobs that were set to PENDING_VALIDATION by the reducer.
        const pendingJobs = jobs.filter((j) => j.status === JobStatus.PENDING_VALIDATION);
        if (pendingJobs.length > 0) {
          const groupName = _select(lapi, getGroupNames)[groupID] ?? 'Individual files';
          // Clone objects to avoid mutating store state directly while running validation/checks
          const pendingCopies = pendingJobs.map((j) => ({ ...j, status: JobStatus.ENQUEUED }));
          const newJobGroups = [
            {
              groupKey: groupName,
              groupID,
              newJobs: pendingCopies,
            },
          ];

          // Validate filenames first (the same as initial upload flow)
          if (!(await validateFiles(newJobGroups))) {
            lapi.dispatch(Notifications.error(Messages.validateFileNamesFail()));
            // Update state with reported invalid names back to jobs
            for (const j of pendingCopies) {
              lapi.dispatch(
                uploaderActions.jobUpdated({
                  jobID: j.uuid,
                  jobProps: { status: j.status, errorMessage: j.errorMessage },
                }),
              );
            }
          } else {
            // Re-check existing conflicts and allow the user to choose overwrite/skip.
            await checkExistantFiles(newJobGroups, lapi);
            lapi.signal.throwIfAborted();
            // Apply resulting state changes from the check to the store.
            // NB: `overwrite` must be dispatched too — checkExistantFiles sets it on
            // the copy, and it has to reach the store so uploadSingleFile reads the
            // correct value (otherwise multipart re-prompts the conflict dialog).
            for (const j of pendingCopies) {
              lapi.dispatch(
                uploaderActions.jobUpdated({
                  jobID: j.uuid,
                  jobProps: {
                    status: j.status,
                    errorMessage: j.errorMessage,
                    overwrite: j.overwrite,
                  },
                }),
              );
            }
          }
        }
      }
      let aborted = false;
      // eslint-disable-next-line @typescript-eslint/require-await
      async function startNextJob() {
        if (aborted) return;
        const jobs = _select(lapi, getJobs).filter(
          (job) => job.status === JobStatus.ENQUEUED && !running.has(job.uuid),
        );
        if (jobs.length === 0) return;
        const job = jobs[0];
        running.add(job.uuid);
        void (async () => {
          try {
            await processJob(lapi, job);
            lapi.signal.throwIfAborted();
          } catch (e) {
            // ignore
          } finally {
            running.delete(job.uuid);
            await startNextJob();
          }
        })();
      }
      // Start up to MAX_PARALLEL_UPLOADS jobs
      for (let i = 0; i < MAX_PARALLEL_UPLOADS; i++) {
        await startNextJob();
      }
      // Wait for all jobs to finish
      while (running.size > 0) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        if (lapi.signal.aborted) {
          aborted = true;
          break;
        }
      }
      lapi.dispatch(conflictResolverActions.contextReset());
    },
  );

  lm.takeEvery([uploaderActions.jobRestarted], async function (action, lapi) {
    const jobs = _select(lapi, getJobs);
    const job = jobs.find((j) => j.uuid === action.payload.jobID);

    const jobManuallySkept = job?.status === JobStatus.SKIPPED && job.errorMessage === null;

    console.log('Job restarted:', job?.uuid, 'Manually skipped:', jobManuallySkept);

    if (job) {
      if (job.status === JobStatus.UPLOAD_FAILURE || job.status === JobStatus.BIND_FAILURE) {
        lapi.dispatch(
          uploaderActions.jobUpdated({
            jobID: job.uuid,
            jobProps: {
              status: JobStatus.PENDING_VALIDATION,
              uploadProgress: null,
              remainingTime: null,
              ellapsedTime: null,
              uploadSpeed: null,
            },
          }),
        );
        await processJob(lapi, job);
      } else if (jobManuallySkept) {
        // For manually skipped job restart we should re-run validation/conflict checks
        // and only start the actual upload if the user confirms.
        lapi.dispatch(
          uploaderActions.jobUpdated({
            jobID: job.uuid,
            jobProps: { status: JobStatus.PENDING_VALIDATION },
          }),
        );

        const jobCopy = { ...job, status: JobStatus.ENQUEUED };
        const groupName = _select(lapi, getGroupNames)[job.groupID] ?? 'Individual files';
        const newJobGroups = [{ groupKey: groupName, groupID: job.groupID, newJobs: [jobCopy] }];

        if (!(await validateFiles(newJobGroups))) {
          lapi.dispatch(Notifications.error(Messages.validateFileNamesFail()));
          lapi.dispatch(
            uploaderActions.jobUpdated({
              jobID: job.uuid,
              jobProps: { status: jobCopy.status, errorMessage: jobCopy.errorMessage },
            }),
          );
          return;
        }

        await checkExistantFiles(newJobGroups, lapi);
        lapi.signal.throwIfAborted();

        // apply resulting status. `overwrite` is set on jobCopy by checkExistantFiles
        // and must reach the store as well.
        lapi.dispatch(
          uploaderActions.jobUpdated({
            jobID: job.uuid,
            jobProps: {
              status: jobCopy.status,
              errorMessage: jobCopy.errorMessage,
              overwrite: jobCopy.overwrite,
            },
          }),
        );

        if (jobCopy.status === JobStatus.ENQUEUED) {
          // Use jobCopy (carries the resolved `overwrite`), not the stale `job`
          // selected before the conflict check.
          await processJob(lapi, jobCopy);
        }
      }
    }
  });
}

const getNonConflictingName = (name: string, nodes: INode[]) => {
  let selectedFolderName = name;
  let counter = 0;
  const findNode = (folderName: string) => nodes.find((node) => node.name === folderName);
  while (findNode(selectedFolderName)) {
    selectedFolderName = `${name} ${++counter}`;
  }
  return selectedFolderName;
};
const getRootFolder = (relpath: string) => {
  return relpath.split(SEP)[0];
};

async function getMappings(
  lapi: ListenerApiLight,
  files: FlattenedDroppedData,
  parentNodeID: number,
): Promise<TSagaReturn<GetMappingsEntry[]>> {
  try {
    // I: list all relative paths, find their roots
    // 1A: list of all relative paths: ["", "path1", "path2", "path1/path11", "path1/path11"]
    const relativePaths = Object.keys(files);
    // 1B: list of all root folders: ["", "path1", "path2", "path11", "path11"]
    const rootsForRelpaths = relativePaths.map(getRootFolder);
    // 1C: join 1A to 1B: {"": "", .... , "path1/path11": "path11"}
    const relpathToRootMap = zipObject(relativePaths, rootsForRelpaths);

    // II: get all roots, find non-conflicting alternatives
    const uniqRoots = [...new Set(rootsForRelpaths)];

    const listing = await apiLoadNodes2(lapi, parentNodeID);
    lapi.signal.throwIfAborted();
    if (!listing.success) {
      return {
        success: false,
        args: { files, parentNodeID },
        reason: Interrupt.Exception('Failed to load nodes in ' + parentNodeID),
      };
    }

    const nonConflictingRootsMap = zipObject(
      uniqRoots,
      uniqRoots.map((root) => getNonConflictingName(root, listing.data.response.nodes)),
    );

    // III: swap relativePaths in `files` with their non-conflicting alternatives
    const nonConflictingFiles = mapKeys(files, (v, relpath) => {
      const root = nonConflictingRootsMap[relpathToRootMap[relpath]];
      const parts = relpath.split(SEP);
      return [root, ...parts.slice(1)].join(SEP);
    });

    // IV: creating folders

    const nonConflictingRelpaths = Object.keys(nonConflictingFiles);
    // removing "" entry (just files without folders) as we don't need to create a folder for it.
    const nonEmptyRelpaths = nonConflictingRelpaths.filter((x) => x.length);
    let mapping: Record<string, number> = {};
    if (nonEmptyRelpaths.length) {
      const mappingResult = await api._unsorted.mapFoldersScenario(parentNodeID, nonEmptyRelpaths);
      lapi.signal.throwIfAborted();
      if (!mappingResult.success) {
        return {
          success: false,
          args: { files, parentNodeID },
          reason: Interrupt.Exception('Failed to map folders: ' + mappingResult.reason.message),
        };
      }
      mapping = mappingResult.data.response;
    }

    // V: unwrap files
    const list: GetMappingsEntry[] = flatMap(nonConflictingRelpaths, (relpath) =>
      nonConflictingFiles[relpath].map((file) => {
        return {
          file,
          targetNodeID: mapping[relpath] ?? parentNodeID,
          targetRoot: getRootFolder(relpath),
        };
      }),
    );

    return {
      success: true,
      args: { files, parentNodeID },
      data: list,
    };
  } catch (x) {
    return {
      success: false,
      args: { files, parentNodeID },
      reason: Interrupt.Exception((x as Error).message),
    };
  }
}
async function uploadFolder(lapi: ListenerApiLight, files: FlattenedDroppedData) {
  // logging start
  for (const key in files) {
    if (key === '') {
      const plainFiles = files[''];
      for (const _ of plainFiles) {
        logger.lapi(lapi, {
          action: 'Upload',
          sub_action: 'File',
          // extra: { filename: plainFile.name, size: plainFile.size },
        });
      }
      // top-level files
    } else {
      logger.lapi(lapi, {
        action: 'Upload',
        sub_action: 'Folder',
        // extra: { name: key },
      });
    }
  }
  // logging end
  const parentNodeID = _select(lapi, getNodeId);
  const breadcrumbs = _select(lapi, getBreadcrumbs);
  const parentIsAssetLab = breadcrumbs.some((bc) => bc.flags.includes(NodeFlag.assetlab));
  if (parentNodeID === null) return;
  // mapping request tracking
  const mappingRequestID = uuid();
  lapi.dispatch(uploaderActions.mappingRequestStarted({ uuid: mappingRequestID }));
  const mappings = await getMappings(lapi, files, parentNodeID);
  lapi.signal.throwIfAborted();
  lapi.dispatch(uploaderActions.mappingRequestFinished({ uuid: mappingRequestID }));
  // mapping request tracking END

  if (!mappings.success) {
    // Типовая причина отказа создания сабфолдеров — недопустимые символы в имени
    // папки; для этого случая показываем понятное описание (MYUA-16279).
    const invalidFolderName = Object.keys(files)
      .flatMap((relpath) => relpath.split(SEP))
      .some(hasInvalidNameChars);
    lapi.dispatch(
      Notifications.error(
        invalidFolderName ? Messages.mapFoldersInvalidName() : Messages.mapFoldersFail(),
      ),
    );
    return;
  }

  const groups = groupBy(mappings.data, (x) => x.targetRoot);
  const newJobGroups: { groupKey: string; groupID: string; newJobs: Job[] }[] = [];

  for (const groupKey of Object.keys(groups)) {
    const groupID = uuid();
    const newJobs = groups[groupKey].map((entry) =>
      makeJob(entry.file, entry.targetNodeID, groupID, parentIsAssetLab),
    );
    newJobGroups.push({ groupKey, groupID, newJobs });
  }

  if (!(await validateFiles(newJobGroups))) {
    lapi.dispatch(Notifications.error(Messages.validateFileNamesFail()));
    return;
  }

  lapi.dispatch(fileManagerActions.refreshRequested());

  await checkExistantFiles(newJobGroups, lapi);

  newJobGroups.forEach((x) => {
    insertJobs(lapi, x.newJobs);
    lapi.dispatch(
      uploaderActions.groupInserted({
        groupID: x.groupID,
        groupName: x.groupKey,
      }),
    );
  });
}

async function validateFiles(groups: { newJobs: Job[] }[]) {
  // Flatten all filenames from all groups for API validation
  const allFilenames = groups.flatMap((group) => group.newJobs.map((job) => job.fileName));
  const validationResponse = await api.fs.validateFilenames(allFilenames);

  if (!validationResponse.success) return false;

  const validationResults = validationResponse.data.response.validation_result;

  let resultIndex = 0; // Track position in validation results

  for (const group of groups) {
    const groupJobs = group.newJobs;
    const groupResult = validationResults.slice(resultIndex, resultIndex + groupJobs.length);
    resultIndex += groupJobs.length;

    // Check if any file in this group is invalid
    const hasInvalid = groupResult.some((res) => !!res.error);

    groupJobs.forEach((job, index) => {
      const res = groupResult[index];

      if (hasInvalid) {
        // If group contains invalid files, skip valid ones
        job.status = res.error ? JobStatus.INVALID_FILE_NAME : JobStatus.SKIPPED;
        if (res.error) job.errorMessage = res.error;
      } else {
        // Otherwise, mark individual validity
        job.status = res.error ? JobStatus.INVALID_FILE_NAME : JobStatus.ENQUEUED;
        if (res.error) job.errorMessage = res.error;
      }
    });
  }

  return true;
}

// Checks for name conflicts between uploading jobs and existing files in the target folder.
// Shows a dialog for each conflict and updates job status if user selects skip.
async function checkExistantFiles(
  newJobGroups: { groupKey: string; groupID: string; newJobs: Job[] }[],
  lapi: ListenerApiLight,
) {
  for (const group of newJobGroups) {
    const jobsByTarget: Record<number, Job[]> = {};
    for (const job of group.newJobs) {
      if (!jobsByTarget[job.parent_node_id]) jobsByTarget[job.parent_node_id] = [];
      jobsByTarget[job.parent_node_id].push(job);
    }
    // For each target folder, check for conflicts
    for (const [parentNodeIDStr, jobs] of Object.entries(jobsByTarget)) {
      const parentNodeID = Number(parentNodeIDStr);
      // Load current nodes in the folder
      const result = await apiLoadNodes2(lapi, parentNodeID);
      lapi.signal.throwIfAborted && lapi.signal.throwIfAborted();
      if (!result.success) continue;
      const existingNames = new Set(result.data.response.nodes.map((n) => n.name));
      for (const job of jobs) {
        if (existingNames.has(job.fileName) && job.status === JobStatus.ENQUEUED) {
          // Show dialog and get user decision
          const decision = await resolve(lapi, job.fileName);
          lapi.signal.throwIfAborted && lapi.signal.throwIfAborted();
          if (decision === AutoConflictResolution.Skip) {
            job.status = JobStatus.SKIPPED;
          } else if (decision === AutoConflictResolution.Overwrite) {
            // Resolve overwrite up-front and carry the decision on the job so it
            // reaches create_multipart_upload (the old late bindFile(force) step
            // no longer exists). Job stays ENQUEUED.
            job.overwrite = true;
          }
        }
      }
    }
  }
}

function insertJobs(lapi: ListenerApiLight, jobs: Job[]) {
  const currentJobs = _select(lapi, getJobs);
  const [clean, skipped] = partition(jobs, (newJob) => {
    return currentJobs.find((job) => makeKey(job) === makeKey(newJob)) === undefined;
  });
  if (skipped.length) {
    lapi.dispatch(
      Notifications.warning({
        title: 'Duplicate files skipped. Remove them from upload menu before uploading again',
        message: skipped.map((x) => `"${x.fileName}"`).join(', '),
      }),
    );
  }
  lapi.dispatch(
    uploaderActions.jobsInserted({
      jobs: clean,
    }),
  );

  return clean;
}

async function processJob(lapi: ListenerApiLight, job: Job): Promise<TSagaReturn<INode>> {
  lapi.dispatch(
    uploaderActions.jobUpdated({
      jobID: job.uuid,
      jobProps: {
        status: JobStatus.UPLOAD_INPROGRESS,
      },
    }),
  );

  const uploadResultSagaReturn = await uploadScenario(lapi, job);
  lapi.signal.throwIfAborted();

  if (!uploadResultSagaReturn.success) {
    lapi.dispatch(
      uploaderActions.jobUpdated({
        jobID: job.uuid,
        jobProps: {
          status: JobStatus.UPLOAD_FAILURE,
        },
      }),
    );
    return {
      reason: Interrupt.API,
      success: false,
      args: { job },
    };
  }

  const createdNode = uploadResultSagaReturn.data;

  // `null` means the job was skipped by the user on a name conflict; the SKIPPED
  // status is already dispatched inside the upload scenario, so don't override it.
  if (!createdNode) {
    return {
      reason: Interrupt.API,
      success: false,
      args: { job },
    };
  }

  // Both single-part and multipart uploads now return the created INode directly
  // (the backend creates the node during upload — there is no separate bind step).
  const currentNode = _select(lapi, getNodeId);
  if (job.parent_node_id === currentNode) {
    const newNode = INodeToCHNode(createdNode);
    lapi.dispatch(fileManagerActions.nodeAdded({ node: newNode }));
  }

  lapi.dispatch(
    uploaderActions.jobUpdated({
      jobID: job.uuid,
      jobProps: {
        status: JobStatus.TAG_REMINDER,
        createdNode: createdNode,
      },
    }),
  );

  logUploadComplete(lapi, job, createdNode.id);
  // Upload succeeded terminally — the file is on the server and this job can no
  // longer be retried, so release the File reference from the registry.
  dropFile(job.uuid);
  return {
    success: true,
    args: { job },
    data: createdNode,
  };
}

function logUploadComplete(lapi: ListenerApiLight, jobToLog: Job, nodeId: number) {
  // fetching latest job from state to ensure we log the most recent data
  const job = _select(lapi, getJobs).find((j) => j.uuid === jobToLog.uuid) ?? jobToLog;

  logger.lapi(lapi, {
    action: 'Upload',
    sub_action: 'Complete',
    extra: {
      int_1: nodeId,
      text_1: job.fileName,
      int_2: job.ellapsedTime,
      int_3: job.fileSize,
    },
  });
}
